/**
 * "Manage" per model op de Posting Bot-pagina.
 *
 * Doet wat de bundle daar doet: de wachtrij van dat model tonen, per item de
 * titel/beschrijving kunnen wijzigen (PATCH /posting/queue-item/{id}), items
 * verwijderen (DELETE /posting/queue-item/{id}), de posted-status resetten
 * (DELETE /posting/queue/{model}/clear?clear_posted=false) of de hele
 * wachtrij legen (…?clear_posted=true), plus het uploaden van een CSV met
 * titel/beschrijving/tags en de foto's.
 */
import { useCallback, useEffect, useState } from 'react'
import { Modal, Field } from './Modal'
import { Empty, Loading, Table, Tag } from './ui'
import { useApi } from '../lib/useApi'
import { api, getToken, mediaToken } from '../lib/api'
import { useActions, mut } from '../lib/actions'
import { dt, num } from '../lib/format'

/** Hoeveel foto's per uploadverzoek; bepaalt hoe vaak de teller oploopt. */
const PAKKET = 10

/** POST met voortgang van het versturen (fetch kan dat niet melden). */
function postMetVoortgang<T>(url: string, fd: FormData,
                             opVoortgang: (deel: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`)
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && ev.total > 0) opVoortgang(ev.loaded / ev.total)
    }
    xhr.onload = () => {
      let body: unknown = {}
      try { body = JSON.parse(xhr.responseText || '{}') } catch { body = {} }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as T)
      else reject(new Error((body as { detail?: string })?.detail || `Media: HTTP ${xhr.status}`))
    }
    xhr.onerror = () => reject(new Error('Media: network error'))
    xhr.send(fd)
  })
}

/**
 * Kleine letters, spaties weg, ontdubbeld, volgorde behouden.
 *
 * Dezelfde regel als `normaliseer` in src/services/posting/tags.py. Hij staat
 * hier nog een keer omdat het scherm meteen moet laten zien wat er straks naar
 * FanCentro gaat; de server keurt het bij het opslaan opnieuw, dus dit is een
 * voorproefje en geen tweede waarheid.
 */
function tagLijst(rauw: string): string[] {
  const uit: string[] = []
  for (const deel of rauw.split(/[,|]/)) {
    const t = deel.trim().toLowerCase().replace(/\s+/g, '')
    if (t && !uit.includes(t)) uit.push(t)
  }
  return uit
}

interface QueueItem {
  id: number; title: string; description: string | null; tags: string | null
  /** De API geeft alleen de bestandsnaam terug, niet het volledige pad. */
  media_filename: string | null; posted: boolean; posted_at: string | null
  queue_order: number; audience: string | null
}

/** Wat de vervangroute teruggeeft. */
type Vervanging = {
  csv_regels: number; gekoppeld: number; csv_zonder_rij: number
  rijen_zonder_csv: number; al_overgedragen: number; nu_aan_het_posten: number
  per_veld: { titles: number; descriptions: number; tags: number }
  onbekende_rijnummers: number[]
  geweigerd: { queue_order: number; media: string }[]
  tags_geweigerd: { queue_order: number; reden: string }[]
  changed?: number
  backup?: string
}

/** Het miniatuurtje in de wachtrij. Laadt pas als de rij in beeld komt, en
 *  valt terug op een teken als er geen plaatje van te maken is. */
function Mini({ itemId, naam, onOpen }: {
  itemId: number; naam: string; onOpen?: () => void
}) {
  const [stuk, setStuk] = useState(false)
  const video = /\.(mp4|mov|m4v|avi|mkv|webm)$/i.test(naam)
  // 09-09 (Jordy): op een miniatuur van veertig pixels kun je niet zien of de
  // foto bij de tekst past. Klikken opent het bewerkvenster met de foto op
  // ware grootte erin, zodat beoordelen en bijstellen op één plek gebeurt.
  const klik = onOpen
    ? { onClick: onOpen, style: { cursor: 'zoom-in' as const },
        title: `${naam} — click to preview and edit` }
    : { title: naam }
  if (stuk) {
    return <div className="minileeg" {...klik}>{video ? '▶' : '▤'}</div>
  }
  return (
    <img
      className="mini"
      alt={naam}
      loading="lazy"
      src={`/api/admin/posting/queue-item/${itemId}/thumbnail`
           + `?token=${encodeURIComponent(mediaToken())}`}
      onError={() => setStuk(true)}
      {...klik}
    />
  )
}

/** De foto op ware grootte in het bewerkvenster.
 *
 *  Geen herschaling en geen hercompressie aan de serverkant: dit is het
 *  venster waarin Jordy beoordeelt of een foto de deur uit mag, en een
 *  bewerkte foto beoordelen is geen beoordeling. Video's spelen hier gewoon
 *  af — een still zegt te weinig over een clip.
 */
function mediaBron(itemId: number): string {
  return `/api/admin/posting/queue-item/${itemId}/preview`
    + `?token=${encodeURIComponent(mediaToken())}`
}

function isVideo(naam: string): boolean {
  return /\.(mp4|mov|m4v|avi|mkv|webm)$/i.test(naam)
}

function Preview({ itemId, naam, onGroot }: {
  itemId: number; naam: string; onGroot?: () => void
}) {
  const [stuk, setStuk] = useState(false)
  if (stuk) {
    return (
      <div className="mut2" style={{ fontSize: 12, padding: 12 }}>
        {`No preview for ${naam}`}
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'center',
                  background: 'var(--mut)', borderRadius: 8, padding: 6 }}>
      {isVideo(naam) ? (
        // Een video krijgt zijn eigen knoppen; klikken zou hem pauzeren in
        // plaats van vergroten, dus die opent het volledige scherm niet.
        <video src={mediaBron(itemId)} controls
               style={{ maxWidth: '100%', maxHeight: 'calc(62vh / var(--dz, 1))' }}
               onError={() => setStuk(true)} />
      ) : (
        <img src={mediaBron(itemId)} alt={naam} onError={() => setStuk(true)}
             onClick={onGroot}
             title={onGroot ? 'Click to see it full size' : naam}
             style={{ maxWidth: '100%', maxHeight: 'calc(62vh / var(--dz, 1))',
                      objectFit: 'contain',
                      cursor: onGroot ? 'zoom-in' : 'default' }} />
      )}
    </div>
  )
}

/** De media op ware grootte, over het hele scherm.
 *
 *  `object-fit: contain` en geen vaste afmeting: de foto wordt zo groot als
 *  hij past en nooit opgerekt. Dit is het venster waarin Jordy beoordeelt of
 *  een foto de deur uit mag, dus er wordt niets aan geschaald wat de indruk
 *  kan veranderen.
 */
function VolledigScherm({ itemId, naam }: { itemId: number; naam: string }) {
  return isVideo(naam) ? (
    <video src={mediaBron(itemId)} controls autoPlay
           onClick={(e) => e.stopPropagation()}
           style={{ maxWidth: 'calc(96vw / var(--dz, 1))', maxHeight: 'calc(92vh / var(--dz, 1))' }} />
  ) : (
    <img src={mediaBron(itemId)} alt={naam}
         style={{ maxWidth: 'calc(96vw / var(--dz, 1))', maxHeight: 'calc(92vh / var(--dz, 1))', objectFit: 'contain' }} />
  )
}

/** Wat `/api/admin/posting/queue/:id` teruggeeft. `total` telt de regels die
 *  aan het filter voldoen (standaard: nog niet gepost), `queue_*` gaat over de
 *  hele wachtrij. */
type Wachtrij = {
  data: QueueItem[]
  total?: number
  queue_total?: number
  queue_text_count?: number
  queue_photo_count?: number
  queue_video_count?: number
  /** Wat er van dit model op een platform staat en nog op zijn foto wacht.
   *  Staat los van deze wachtrij; zie het bevestigingsvenster van "Delete
   *  queue + photos". */
  vault_backlog?: {
    fc_pending: number; fc_overdue: number
    f2f_pending: number; f2f_overdue: number
    oldest_overdue: string | null
  }
}

/** Wat de backend accepteert (`upload_media_files`): jpg, jpeg, png, gif, webp,
 *  mp4, mov. Hier gecontroleerd zodat een verkeerd bestand meteen een nette
 *  melding geeft in plaats van een fout van de server. */
const TOEGESTANE_MEDIA = /\.(jpe?g|png|gif|webp|mp4|mov)$/i
const MAX_BESTAND_MB = 2000
const MAX_BESTAND_BYTES = MAX_BESTAND_MB * 1024 * 1024

export function ManageDialog({
  modelId,
  modelName,
  cycleLabel,
  onClose,
  onChanged,
}: {
  modelId: number
  modelName: string
  /** "cycle 3 · item 3 of 76"; komt uit dezelfde configrespons als het overzicht. */
  cycleLabel?: string
  onClose: () => void
  onChanged: () => void
}) {
  // `limit=500`: de route geeft standaard 50 terug, en dan telde de kop 61
  // items terwijl de lijst er 50 toonde — dat leest als "er is iets kwijt".
  // 500 is de bovengrens die de route toestaat en ruim boven een volle
  // wachtrij (96 bij de grootste modellen).
  const q = useApi<QueueItem[] | Wachtrij>(
    `/api/admin/posting/queue/${modelId}?limit=500&show_posted=true`, [modelId])
  const rows = Array.isArray(q.data) ? q.data : (q.data?.data ?? [])
  const bron = Array.isArray(q.data) ? undefined : q.data
  const { run, busy } = useActions()

  /** Waaruit de wachtrij bestaat — geteld door de server, over de HELE rij.
   *
   *  Stond hier eerst als een telling over `rows`. Dat is maar één pagina van
   *  vijftig, dus bij Alice met 96 items las de kop "50 items · 50 texts" en
   *  leek de rij half zo groot als hij is. De server telt nu met één query, en
   *  het overzicht gebruikt exact dezelfde getallen.
   *
   *  "texts" is een regel met een titel óf omschrijving — wat er straks bij de
   *  foto staat. Nadrukkelijk niet "regels zonder media": dan las je bij
   *  vijftig foto's met vijftig teksten "0 texts". */
  const tellers = {
    texts: bron?.queue_text_count ?? 0,
    photos: bron?.queue_photo_count ?? 0,
    videos: bron?.queue_video_count ?? 0,
    posted: Math.max((bron?.queue_total ?? 0) - (bron?.total ?? 0), 0),
  }
  const totaal = bron?.queue_total ?? rows.length
  /** "cycle 3 · item 3 of 76" — meegegeven door de aanroeper, zodat de kop
   *  hetzelfde toont als de PROGRESS-kolom in het overzicht. */
  const cyclusLabel = cycleLabel ?? ''
  const media = tellers.photos + tellers.videos
  /** De opruimplicht van dit model, en die staat LOS van deze wachtrij.
   *  Posts die al op FanCentro of F2F staan hebben hun foto nog in de vault;
   *  die verdwijnt pas als de opruiming draait. Deze wachtrij legen doet daar
   *  niets aan — daarom staat het getal in de bevestiging, zodat zichtbaar is
   *  wat er blijft staan en dat dat de bedoeling is. */
  const achterstand = {
    aantal: (bron?.vault_backlog?.fc_pending ?? 0)
      + (bron?.vault_backlog?.f2f_pending ?? 0),
    oudste: bron?.vault_backlog?.oldest_overdue ?? null,
  }
  const blijftStaan = achterstand.aantal
  /** Een tekst zonder foto wordt nooit gepost, en een foto zonder tekst gaat
   *  zonder onderschrift weg. Allebei wil je zien vóórdat je op Start drukt. */
  const scheef = tellers.texts !== media

  const [edit, setEdit] = useState<QueueItem | null>(null)
  const [title, setTitle] = useState('')
  const [desc, setDesc] = useState('')
  const [tags, setTags] = useState('')
  /** Welke tags FanCentro aanbiedt. Komt van de server, zodat het scherm en
   *  het postpad nooit uit de pas lopen. Zie /api/admin/posting/tagproef. */
  const proef = useApi<{ approved: string[]; rejected: string[]
                         min_tags: number; max_tags: number }>(
    '/api/admin/posting/tagproef')
  const [uploading, setUploading] = useState(false)
  /** Voortgang van de foto-upload (Jordy, 15-09: 132 foto's voor Natalia en het
   *  scherm bleef op "Uploading…" staan zonder te laten zien dat er iets
   *  gebeurde). `klaar` telt de foto's die de server al heeft verwerkt;
   *  `deel` is hoe ver het lopende pakketje is (0..1). */
  const [voortgang, setVoortgang] = useState<{ klaar: number; totaal: number; deel: number } | null>(null)
  const [uploadMsg, setUploadMsg] = useState('')

  /** Volledig scherm voor de media; leeg is dicht. */
  const [groot, setGroot] = useState<QueueItem | null>(null)

  /** Wat er stond toen het venster openging.
   *
   *  Nodig om te kunnen waarschuwen bij het springen naar een andere rij: wie
   *  een tekst heeft aangepast en dan op de pijltjestoets drukt, is die anders
   *  kwijt zonder het te merken. */
  const [origineel, setOrigineel] = useState({ title: '', desc: '', tags: '' })

  function tagsAlsTekst(item: QueueItem): string {
    // De wachtrij bewaart tags met een liggend streepje ertussen; op het
    // scherm leest een komma prettiger. Bij het opslaan gaat het weer terug.
    return (item.tags ?? '').split('|').filter(Boolean).join(', ')
  }

  function startEdit(item: QueueItem) {
    const t = item.title ?? ''
    const d = item.description ?? ''
    const g = tagsAlsTekst(item)
    setEdit(item); setTitle(t); setDesc(d); setTags(g)
    setOrigineel({ title: t, desc: d, tags: g })
  }

  /** Is er iets gewijzigd dat nog niet is opgeslagen? */
  const gewijzigd = !!edit && (
    title !== origineel.title || desc !== origineel.desc
    || tags !== origineel.tags)

  /** Naar de vorige of volgende rij in de wachtrij, zonder te sluiten.
   *
   *  Waarschuwt eerst als er iets openstaat. Dat is met opzet een `confirm` en
   *  geen stilzwijgend opslaan: opslaan wat iemand misschien nog aan het
   *  proberen was, is erger dan het weggooien met een vraag erbij. */
  const spring = useCallback((richting: -1 | 1) => {
    if (!edit) return
    const i = rows.findIndex((r) => r.id === edit.id)
    if (i < 0) return
    const volgende = rows[i + richting]
    if (!volgende) return
    if (gewijzigd && !window.confirm(
      'You have unsaved changes to this item. Leave them behind?')) return
    startEdit(volgende)
  }, [edit, rows, gewijzigd, title, desc, tags, origineel])

  /** Pijltjestoetsen springen; Esc sluit het volledige scherm.
   *
   *  De toetsen doen niets zolang de cursor in een tekstveld staat — anders
   *  kun je niet meer door een titel heen lopen zonder van rij te wisselen. */
  useEffect(() => {
    if (!edit) return
    const opToets = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && groot) { setGroot(null); return }
      const doel = e.target as HTMLElement | null
      const tikt = doel && (doel.tagName === 'INPUT'
        || doel.tagName === 'TEXTAREA' || doel.isContentEditable)
      if (tikt) return
      if (e.key === 'ArrowLeft') { e.preventDefault(); spring(-1) }
      if (e.key === 'ArrowRight') { e.preventDefault(); spring(1) }
    }
    window.addEventListener('keydown', opToets)
    return () => window.removeEventListener('keydown', opToets)
  }, [edit, groot, spring])

  /** Alleen de teksten vervangen; foto's en koppeling blijven staan.
   *
   *  Het bestand gaat NAAR DE SERVER en wordt daar gelezen, met dezelfde lezer
   *  als "Upload CSV + photos" (src/services/posting/csv_wachtrij.py). Hier
   *  stond eerst een eigen lezer in de browser, en twee lezers voor hetzelfde
   *  formaat is precies hoe de streepjesfout van 10-09 kon ontstaan: de een
   *  wist dat een `|` tags scheidt en de ander niet.
   */
  async function vervangTeksten(bestand: File | null) {
    if (!bestand) return

    async function stuur(dryRun: boolean) {
      const fd = new FormData()
      fd.append('file', bestand!)
      return api.postForm<Vervanging>(
        `/api/admin/posting/queue/${modelId}/vervang-teksten-csv`
        + `?dry_run=${dryRun}`, fd)
    }

    let proef: Vervanging | null = null
    try {
      proef = await stuur(true)
    } catch (e) {
      // De server weigert een bestand met een verkeerde kop, met de gevonden
      // en de verwachte kolomnamen erbij. Die melding hoort ongeknipt op het
      // scherm: zonder de kolomnamen is een kopfout niet op te lossen.
      setUploadMsg(String((e as Error)?.message || e))
      return
    }
    if (!proef) { setUploadMsg('Could not read the preview'); return }
    const regels = [
      `${proef.gekoppeld} rows will get new text`,
      `${proef.csv_zonder_rij} lines in the file have no matching row`
        + (proef.onbekende_rijnummers.length
           ? ` (${proef.onbekende_rijnummers.slice(0, 12).join(', ')})` : ''),
      `${proef.rijen_zonder_csv} rows are not in the file and stay as they are`,
      `${proef.al_overgedragen} of them are already on FanCentro — their text`
        + ' there does NOT change, only our copy',
      `${proef.nu_aan_het_posten} rows are being posted right now and are`
        + ' skipped',
    ]
    if (proef.tags_geweigerd.length) {
      regels.push(`${proef.tags_geweigerd.length} lines were refused on their`
                  + ' tags and are skipped')
    }
    const v = proef.per_veld
    await run({
      label: 'Replace texts',
      confirm: `Replace the texts of ${proef.gekoppeld} rows?\n\n`
        + `  titles: ${v.titles} · descriptions: ${v.descriptions}`
        + ` · tags: ${v.tags}\n\n`
        + regels.map((r) => `  ${r}`).join('\n')
        + '\n\nPhotos, the link between row and photo, and the posted status'
        + ' stay exactly as they are. The old texts are backed up first.',
      request: async () => {
        const uit = await stuur(false)
        setUploadMsg(`Replaced ${uit?.changed ?? 0} texts. `
                     + `Old texts saved to ${uit?.backup ?? '?'}`)
      },
      onDone: () => { q.refetch(); onChanged() },
    })
  }

  /** De teksten leegmaken; foto's blijven staan. */
  const leegTeksten = async () => {
    const proef = await api.post<{
      leeg_te_maken: number; fotos_blijven: number; posted_blijft: number
      nu_aan_het_posten: number; totaal_rijen: number
    }>(`/api/admin/posting/queue/${modelId}/teksten-leegmaken?dry_run=true`, {})
    if (!proef) { setUploadMsg('Could not read the preview'); return }
    if (!proef.leeg_te_maken) { setUploadMsg('There are no texts to clear'); return }
    await run({
      label: 'Clear texts',
      confirm: `Clear the text of ${proef.leeg_te_maken} rows?\n\n`
        + `  ${proef.fotos_blijven} photos stay, still linked to the same`
        + ' row number\n'
        + `  ${proef.posted_blijft} rows keep their posted status\n`
        + `  ${proef.nu_aan_het_posten} rows are being posted right now and`
        + ' are skipped\n\n'
        + 'Title, description and tags are emptied so you can upload a fresh'
        + ' CSV. The old texts are backed up first.',
      request: async () => {
        const uit = await api.post<{ changed?: number; backup?: string }>(
          `/api/admin/posting/queue/${modelId}/teksten-leegmaken?dry_run=false`,
          {})
        setUploadMsg(`Cleared ${uit?.changed ?? 0} texts. `
                     + `Old texts saved to ${uit?.backup ?? '?'}`)
      },
      onDone: () => { q.refetch(); onChanged() },
    })
  }

  /** CSV + foto's in één keer, zoals de bundle: multipart naar de queue. */
  /** CSV én foto's uploaden.
   *
   *  Dit riep één pad aan — `/queue/:id/upload` — dat niet bestaat. Omdat
   *  `/queue/:id` wél bestaat (als GET), gaf de server 405 Method Not Allowed in
   *  plaats van 404, en dat las op het scherm als "Upload failed: Method Not
   *  Allowed" zonder verdere aanwijzing.
   *
   *  De backend heeft twee routes: de CSV gaat naar `/upload-csv` (veld `file`)
   *  en de media naar `/upload-media` (veld `files`). Ze worden hier na elkaar
   *  aangeroepen: eerst de CSV, want die maakt de wachtrijregels aan, dan de
   *  foto's die eraan gekoppeld worden. */
  async function upload(files: FileList | null) {
    if (!files || !files.length) return
    const alles = Array.from(files)
    const csv = alles.filter((f) => f.name.toLowerCase().endsWith('.csv'))
    const media = alles.filter((f) => TOEGESTANE_MEDIA.test(f.name))
    const rest = alles.filter((f) => !csv.includes(f) && !media.includes(f))

    if (rest.length) {
      setUploadMsg(`Not uploaded — unsupported file type: ${rest.map((f) => f.name).join(', ')}`)
      return
    }
    const teGroot = alles.filter((f) => f.size > MAX_BESTAND_BYTES)
    if (teGroot.length) {
      setUploadMsg(`Not uploaded — over ${MAX_BESTAND_MB} MB: ${teGroot.map((f) => f.name).join(', ')}`)
      return
    }

    setUploading(true); setUploadMsg('')
    const meldingen: string[] = []
    try {
      if (csv.length) {
        for (const f of csv) {
          const fd = new FormData()
          fd.append('file', f)
          const res = await fetch(`/api/admin/posting/queue/${modelId}/upload-csv`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${getToken()}` },
            body: fd,
          })
          if (!res.ok) {
            const j = await res.json().catch(() => ({} as { detail?: string }))
            throw new Error(j?.detail || `CSV: HTTP ${res.status}`)
          }
          // De route antwoordt met {message, count, without_photo_count,
          // rows_without_photo}. 06-10: "Uploaded: 204 rows" terwijl er niets
          // was opgeslagen — 0 opgeslagen is nu een fout, geen succes.
          const out = await res.json().catch(() => ({} as {
            count?: number; message?: string
            without_photo_count?: number; rows_without_photo?: string[] }))
          if (!out.count) {
            throw new Error(`CSV ${f.name}: nothing saved${out.message ? ` (${out.message})` : ''}`)
          }
          meldingen.push(`${out.count} rows from ${f.name}`)
          // Zonder foto's in deze upload: meteen per rij zeggen welke rijen
          // als tekst in de wachtrij staan.
          if (!media.length && out.without_photo_count) {
            const rijen = out.rows_without_photo ?? []
            meldingen.push(`${out.without_photo_count} without photo (saved as text): `
              + rijen.slice(0, 5).join(', ') + (out.without_photo_count > 5 ? ', …' : ''))
          }
        }
      }
      if (media.length) {
        // In pakketjes van PAKKET foto's in plaats van alles in één verzoek:
        // zo kan het scherm "38 of 132 processed" tonen op basis van wat de
        // server echt heeft verwerkt, met daarbinnen de voortgang van het
        // lopende pakketje. Elke foto wordt op de server los gekoppeld, dus
        // het resultaat is hetzelfde als in één keer.
        let verwerkt = 0
        const overgeslagen: string[] = []
        setVoortgang({ klaar: 0, totaal: media.length, deel: 0 })
        for (let i = 0; i < media.length; i += PAKKET) {
          const stuk = media.slice(i, i + PAKKET)
          const fd = new FormData()
          for (const f of stuk) fd.append('files', f)
          // {message, count, matched, skipped} — `skipped` zijn bestanden zonder
          // bijpassende wachtrijregel; die horen genoemd te worden, anders denk
          // je dat alles gelukt is.
          const out = await postMetVoortgang<{
            count?: number; skipped?: { filename?: string }[] }>(
            `/api/admin/posting/queue/${modelId}/upload-media`, fd,
            (deel) => setVoortgang({ klaar: i, totaal: media.length, deel }))
          verwerkt += out.count ?? stuk.length
          for (const sk of out.skipped ?? []) overgeslagen.push(String(sk.filename ?? ''))
          setVoortgang({ klaar: Math.min(i + stuk.length, media.length),
                         totaal: media.length, deel: 0 })
        }
        meldingen.push(`${verwerkt} file(s)`)
        if (overgeslagen.length) {
          meldingen.push(`${overgeslagen.length} without a matching queue row`)
        }
      }
      // 06-10: wat er na de upload ECHT in de wachtrij staat, zodat een
      // upload die niet aankwam niet als geslaagd leest.
      if (meldingen.length) {
        try {
          const nu = await fetch(`/api/admin/posting/queue/${modelId}?limit=1`, {
            headers: { Authorization: `Bearer ${getToken()}` } })
          if (nu.ok) {
            const d = await nu.json() as { queue_total?: number; queue_text_count?: number
              queue_photo_count?: number; queue_video_count?: number }
            meldingen.push(`queue now: ${d.queue_total ?? 0} items · ${d.queue_text_count ?? 0} texts · `
              + `${d.queue_photo_count ?? 0} photos · ${d.queue_video_count ?? 0} videos`)
          }
        } catch { /* alleen extra informatie */ }
      }
      setUploadMsg(meldingen.length ? `Uploaded: ${meldingen.join(' · ')}` : 'Nothing to upload')
      q.refetch(); onChanged()
    } catch (e) {
      setUploadMsg(`Upload failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setUploading(false)
      setVoortgang(null)
    }
  }

  return (
    <Modal
      title={`Manage — ${modelName}`}
      desc={`Upload a CSV (title, description, tags) plus photos numbered 1.jpg, 2.jpg… `
        + `Accepted: CSV, jpg, png, gif, webp, mp4, mov — up to ${MAX_BESTAND_MB} MB each. `
        + `The queue below is what the bot works through.`}
      width={900}
      onClose={onClose}
      footer={<button className="btn ghost sm" type="button" onClick={onClose}>Close</button>}
    >
      {voortgang ? (
        <div style={{ marginBottom: 10 }}>
          <div className="mut2" style={{ fontSize: 12, marginBottom: 4 }}>
            {`${voortgang.klaar} of ${voortgang.totaal} photos processed`}
            {voortgang.deel > 0
              && voortgang.deel < 1
              ? ` · sending next ${Math.min(PAKKET, voortgang.totaal - voortgang.klaar)} (${Math.round(voortgang.deel * 100)}%)`
              : voortgang.klaar < voortgang.totaal ? ' · saving on the server…' : ''}
          </div>
          <div style={{ height: 8, borderRadius: 4, background: 'var(--mut)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', background: 'var(--acc)',
              width: `${Math.min(100, ((voortgang.klaar + voortgang.deel * Math.min(PAKKET, voortgang.totaal - voortgang.klaar)) / voortgang.totaal) * 100)}%`,
              transition: 'width .2s',
            }} />
          </div>
        </div>
      ) : null}
      <div className="row" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
        <label className="btn primary sm" style={{ cursor: 'pointer' }}>
          {uploading ? (voortgang ? `Uploading ${voortgang.klaar} of ${voortgang.totaal}…` : 'Uploading…') : 'Upload CSV + photos'}
          <input
            type="file"
            multiple
            accept=".csv,image/*"
            style={{ display: 'none' }}
            onChange={(e) => upload(e.target.files)}
          />
        </label>
        {/* De wachtrij is de bron; dit bestand is een momentopname. Zelfde
            kolommen als de import, zodat je hem kunt bewerken en via de
            bulkvervanging weer kunt terugzetten zonder de foto's aan te
            raken. */}
        <button
          className="btn ghost sm"
          type="button"
          onClick={async () => {
            // Niet via window.open: die stuurt geen Authorization-header mee
            // en de route zit achter de gewone dependency. Dus zelf ophalen
            // en het bestand in de browser aanbieden.
            const res = await fetch(
              `/api/admin/posting/queue/${modelId}/export-csv`,
              { headers: { Authorization: `Bearer ${getToken() ?? ''}` } })
            if (!res.ok) { setUploadMsg(`Download failed (${res.status})`); return }
            const blob = await res.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `queue-model-${modelId}.csv`
            a.click()
            URL.revokeObjectURL(url)
          }}
        >
          Download CSV
        </button>
        <button
          className="btn ghost sm"
          type="button"
          disabled={busy === 'Reset posted'}
          onClick={() => run({
            label: 'Reset posted',
            confirm: 'Reset posted status? This lets the bot re-post items that were already posted.',
            request: mut.del(`/api/admin/posting/queue/${modelId}/clear?clear_posted=false`),
            onDone: () => { q.refetch(); onChanged() },
            successText: 'Queue reset. Upload new CSV and photos to restart.',
          })}
        >
          Reset posted status
        </button>
        {/* 10-09 (Jordy): "ik wil de CSV kunnen weggooien en opnieuw uploaden
            zonder dat de foto's verdwijnen." Vandaar deze twee naast Clear
            queue: die laatste gooit ook de bestanden op schijf weg. */}
        <label className={`btn ghost sm${busy ? ' disabled' : ''}`}
               style={{ cursor: 'pointer' }}>
          Replace texts (CSV)
          <input
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null
              e.target.value = ''
              vervangTeksten(f)
            }}
          />
        </label>
        <button
          className="btn ghost sm"
          type="button"
          disabled={busy === 'Clear texts'}
          onClick={leegTeksten}
        >
          Clear texts
        </button>
        <button
          className="btn danger sm"
          type="button"
          disabled={busy === 'Clear queue'}
          onClick={() => run({
            label: 'Clear queue',
            /* De bevestiging noemt de getallen. Hiervoor stond er alleen
               "delete ALL queue items", en dan is niet te zien dat de foto's
               van de schijf meegaan — dat is precies waar Jordy tegenaan liep.

               10-09: en het tweede getal erbij. Deze wachtrij legen mag altijd
               en raakt niets aan wat al op een platform staat, maar dat moet je
               kunnen LEZEN. Wat daar staat wacht op zijn eigen opruiming en
               blijft hier volledig buiten. */
            confirm: `${num(totaal)} items removed · ${num(blijftStaan)} kept`
              + ' — already on FanCentro, vault cleanup still pending\n\n'
              + `Removed from this model's queue:\n`
              + `  ${num(totaal)} items\n`
              + `  ${num(tellers.photos + tellers.videos)} media files, deleted`
              + ' from disk as well\n'
              + `  ${num(tellers.texts)} texts\n\n`
              + `Kept: ${num(blijftStaan)} posts of this model that are already`
              + ' on a platform and still have a photo in the vault'
              + (achterstand.oudste
                 ? `, oldest overdue since ${achterstand.oudste.slice(0, 16)}`
                 : '')
              + '. Clearing this queue does not touch them — their cleanup runs'
              + ' on its own.\n\n'
              + 'The link between row number and photo goes with it. If you only'
              + ' want new texts, use Clear texts or Replace texts instead.\n\n'
              + 'This cannot be undone.',
            request: mut.del(`/api/admin/posting/queue/${modelId}/clear?clear_posted=true`),
            onDone: () => { q.refetch(); onChanged() },
            successText: 'Queue cleared completely.',
          })}
        >
          Delete queue + photos
        </button>
        <div className="spacer" style={{ flex: 1 }} />
        {/* Dezelfde uitsplitsing als oud toont: hoeveel er in de rij staan en
            waaruit die bestaat. Alleen "50 items" zegt niet of het foto's of
            teksten zijn, en dat is precies wat je wilt weten voordat je start. */}
        <span className={scheef ? 'mut2 scheef' : 'mut2'} style={{ fontSize: 12 }}>
          {cyclusLabel ? <>{cyclusLabel} · </> : null}
          {num(totaal)} items · {num(tellers.texts)} texts ·{' '}
          {num(tellers.photos)} photos · {num(tellers.videos)} videos
          {tellers.posted ? ` · ${num(tellers.posted)} posted` : ''}
          {scheef ? (
            <> — {tellers.texts > media
              ? `${num(tellers.texts - media)} texts without media`
              : `${num(media - tellers.texts)} media without text`}</>
          ) : null}
        </span>
      </div>

      {uploadMsg ? <div className="note" style={{ marginBottom: 10 }}>{uploadMsg}</div> : null}

      {q.loading ? <Loading /> : !rows.length ? <Empty>No queue items yet</Empty> : (
        <Table cols={[
          { key: 'o', label: '#', num: true }, { key: 'th', label: '' },
          { key: 't', label: 'Title' },
          { key: 'd', label: 'Description' }, { key: 't', label: 'Tags' },
          { key: 'm', label: 'Media' },
          { key: 'p', label: 'Posted' }, { key: 'a', label: 'Actions' },
        ]}>
          {rows.map((it) => (
            <tr key={it.id}>
              <td className="num mono">{it.queue_order}</td>
              <td>
                {it.media_filename
                  ? <Mini itemId={it.id} naam={it.media_filename}
                          onOpen={() => startEdit(it)} />
                  : <span className="dim">—</span>}
              </td>
              <td>{it.title}</td>
              <td className="mut" style={{ maxWidth: 220 }}>{it.description || '—'}</td>
              {/* 09-09 (Jordy): de tags stonden alleen in het bewerkvenster,
                  dus je moest elke rij openklikken om te zien welke er fout
                  stonden. Afgekeurde tags kleuren rood — die worden er bij het
                  inplannen uitgefilterd en de post gaat dan met minder tags
                  de deur uit dan bedoeld. */}
              <td style={{ maxWidth: 200 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {(it.tags ?? '').split('|').filter(Boolean).map((t) => (
                    <Tag key={t}
                         kind={(proef.data?.rejected ?? []).includes(t)
                           ? 'bad' : 'mut'}>
                      {t}
                    </Tag>
                  ))}
                  {!(it.tags ?? '').trim()
                    ? <span className="dim">—</span> : null}
                </div>
              </td>
              <td className="mut2" style={{ fontSize: 12 }}>
                {it.media_filename || '—'}
              </td>
              <td>
                {it.posted
                  ? <Tag kind="ok">{it.posted_at ? dt(it.posted_at) : 'yes'}</Tag>
                  : <span className="dim">no</span>}
              </td>
              <td>
                <div className="row">
                  <button className="btn ghost sm" type="button" onClick={() => startEdit(it)}>
                    Edit
                  </button>
                  <button
                    className="btn ghost sm"
                    type="button"
                    onClick={() => run({
                      label: 'Delete item',
                      confirm: 'Delete this queue item?',
                      request: mut.del(`/api/admin/posting/queue-item/${it.id}`),
                      onDone: () => { q.refetch(); onChanged() },
                    })}
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {edit ? (
        <Modal
          /* 10-09 (Jordy): "ik wil bij het bewerken de foto groot zien om te
             controleren of de tekst erbij klopt." Vandaar een breed venster met
             de media links en de velden rechts, in plaats van een smalle kolom
             waarin de foto een postzegel was. */
          width={1040}
          title={`Item ${edit.queue_order} — ${edit.media_filename || 'no media'}`}
          desc={`${rows.findIndex((r) => r.id === edit.id) + 1} of ${rows.length}`
                + ' in this queue · use ← and → to move between items'}
          onClose={() => {
            if (gewijzigd && !window.confirm(
              'You have unsaved changes. Close anyway?')) return
            setEdit(null)
          }}
          footer={
            <>
              <button
                className="btn ghost sm"
                type="button"
                disabled={rows.findIndex((r) => r.id === edit.id) <= 0}
                onClick={() => spring(-1)}
              >
                ← Previous
              </button>
              <button
                className="btn ghost sm"
                type="button"
                disabled={rows.findIndex((r) => r.id === edit.id) >= rows.length - 1}
                onClick={() => spring(1)}
              >
                Next →
              </button>
              <button className="btn ghost sm" type="button"
                      onClick={() => {
                        if (gewijzigd && !window.confirm(
                          'You have unsaved changes. Close anyway?')) return
                        setEdit(null)
                      }}>
                Cancel
              </button>
              <button
                className="btn primary sm"
                type="button"
                disabled={busy === 'Update item' || !gewijzigd}
                onClick={() => run({
                  label: 'Update item',
                  /* Alleen deze drie velden gaan mee. De foto, de koppeling
                     tussen foto en tekst (`queue_order` -> bestandsnaam) en de
                     status van een post worden hier niet aangeraakt; zie
                     tests/test_bewerkvenster_raakt_alleen_tekst.py. */
                  request: () => api.patch(`/api/admin/posting/queue-item/${edit.id}`,
                                           { title, description: desc,
                                             tags: tagLijst(tags).join('|') }),
                  onDone: () => {
                    setOrigineel({ title, desc, tags })
                    q.refetch(); onChanged()
                  },
                  successText: 'Item updated.',
                })}
              >
                {gewijzigd ? 'Save' : 'Saved'}
              </button>
            </>
          }
        >
          <div className="bewerkraster">
            <div>
              {edit.media_filename ? (
                <>
                  <Preview itemId={edit.id} naam={edit.media_filename}
                           onGroot={() => setGroot(edit)} />
                  <div className="mut2" style={{ fontSize: 11, marginTop: 6,
                                                 textAlign: 'center' }}>
                    Click the photo to see it full size · Esc closes it
                  </div>
                </>
              ) : (
                <div className="mut2" style={{ fontSize: 12, padding: 24,
                                               textAlign: 'center' }}>
                  This row has no media
                </div>
              )}
            </div>
            <div>
              <Field label="Title">
                <input className="inp" value={title}
                       onChange={(e) => setTitle(e.target.value)} />
              </Field>
              <Field label="Description">
                <textarea className="inp" rows={4} value={desc}
                          onChange={(e) => setDesc(e.target.value)} />
              </Field>
              {/* Tags. Dit is het veld dat op 09-09 drie posts liet omvallen:
                  een tag die FanCentro niet aanbiedt wordt geen chip, en met
                  minder dan twee chips weigert hij de post — met een melding
                  die niet zei wélke tag het was. Daarom hier meteen zichtbaar. */}
              <Field label="Tags">
                <input className="inp" value={tags} placeholder="hot, cutie, glow"
                       onChange={(e) => setTags(e.target.value)} />
                {(() => {
                  const lijst = tagLijst(tags)
                  const afgekeurd = lijst.filter(
                    (t) => (proef.data?.rejected ?? []).includes(t))
                  const geldig = lijst.filter((t) => !afgekeurd.includes(t))
                  const min = proef.data?.min_tags ?? 2
                  const max = proef.data?.max_tags ?? 5
                  return (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4,
                                    marginTop: 6 }}>
                        {lijst.map((t) => (
                          <Tag key={t}
                               kind={afgekeurd.includes(t) ? 'bad' : 'ok'}>
                            {t}
                          </Tag>
                        ))}
                      </div>
                      {afgekeurd.length ? (
                        <div className="mut2 scheef" style={{ fontSize: 11,
                                                              marginTop: 4 }}>
                          {`FanCentro does not offer these: ${afgekeurd.join(', ')}`}
                          {' — the post fails on it'}
                        </div>
                      ) : null}
                      {/* 10-09 (Jordy): waarschuw als er ná het schrappen van
                          de afgekeurde tags te weinig overblijft. Die tags
                          worden er bij het inplannen uitgefilterd, dus je ziet
                          er vijf staan terwijl de post er met één de deur uit
                          gaat — en dan weigert FanCentro hem. */}
                      {geldig.length < min ? (
                        <div style={{ fontSize: 11, marginTop: 6, padding: 6,
                                      borderRadius: 6,
                                      background: 'rgba(255,80,80,.12)',
                                      color: 'var(--bad)' }}>
                          {`Only ${geldig.length} usable tag`}
                          {geldig.length === 1 ? '' : 's'}
                          {` left after the rejected ones are dropped. `}
                          {`FanCentro needs at least ${min}, so this post `}
                          {`will be refused.`}
                        </div>
                      ) : null}
                      {lijst.length > max ? (
                        <div className="mut2" style={{ fontSize: 11, marginTop: 4 }}>
                          {`more than ${max}; only the first ${max} are used`}
                        </div>
                      ) : null}
                    </>
                  )
                })()}
              </Field>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* Volledig scherm. Staat buiten het bewerkvenster zodat hij eroverheen
          valt en niet in de schuifbalk ervan verdwijnt. */}
      {groot && groot.media_filename ? (
        <div
          onClick={() => setGroot(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 99999,
                   background: 'rgba(0,0,0,.92)', display: 'flex',
                   alignItems: 'center', justifyContent: 'center', padding: 16,
                   cursor: 'zoom-out' }}
        >
          <VolledigScherm itemId={groot.id} naam={groot.media_filename} />
          <div style={{ position: 'absolute', top: 12, right: 16, fontSize: 12,
                        color: '#bbb' }}>
            {groot.media_filename} · Esc or click to close
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
